/* eslint-disable no-undef */
import React, { useState } from 'react';
import 'resources/styles/chatkit-classify.css';
import type { CrossRulingLink } from 'components/TradeHelper/HtsClassificationChat';

interface AuditType {
  name?: string;
  hts_code?: string;
  cmdt_desc?: string;
  explanatory_notes?: string;
  section_chapter_notes?: string;
  cross_data_links?: CrossRulingLink[];
  isExpands?: boolean;
  saveBtn?: any;
  selectedStep?: any;
  handleRowClick?: any;
}

const ClassifyResultsComp: React.FC<AuditType> = ({
  name,
  hts_code,
  cmdt_desc,
  section_chapter_notes,
  cross_data_links,
  isExpands,
  selectedStep,
  handleRowClick,
}) => {
  // cross_data_links come from live API state and could contain unsafe URLs
  // (e.g. javascript:). Only render anchors for http/https links — same
  // filter Classification.tsx uses.
  const safeCrossLinks = (cross_data_links || []).filter((link) => {
    try {
      const { protocol } = new URL(link.url);
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  });

  const [isExpandedSecNotes, setIsExpandedSecNotes] = useState(false);

  const [isExpand, setIsExpand] = useState(isExpands || false);
  const [isExpandProd, setIsExpandProd] = useState(true);
  const [isExpandSecNotes, setIsExpandSecNotes] = useState(true);

  const [isExpandCross, setIsExpandCross] = useState(true);

  const formatExplanatoryNote = (rawNotes: string) => {
    const cleanedNotes = rawNotes
      .replace(/[\[\]']/g, '')
      .replace(/,(\s)?$/gm, '')
      .replace(/^°/gm, '');

    const lines = cleanedNotes
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const result: JSX.Element[] = [];
    let bulletItems: string[] = [];

    const flushBullets = () => {
      if (bulletItems.length > 0) {
        result.push(
          <ul
            key={`ul-${result.length}`}
            style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}
          >
            {bulletItems.map((item, i) => (
              <li
                key={i}
                style={{ marginBottom: '0.25rem' }}
                dangerouslySetInnerHTML={{
                  __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                }}
              />
            ))}
          </ul>,
        );
        bulletItems = [];
      }
    };

    lines.forEach((line, index) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      const isHeading =
        /^\*\*/.test(line) || (/^[A-Z\s]+$/.test(line) && line.length < 80);
      const isBullet = /^[-•*]|\(\w\)|^\(\d+\)|^\d+\)/.test(line);

      if (isHeading) {
        flushBullets();
        result.push(
          <h2
            key={`h-${index}`}
            style={{ margin: '0.5rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      } else if (isBullet) {
        bulletItems.push(formatted);
      } else {
        flushBullets();
        result.push(
          <p
            key={`p-${index}`}
            style={{ margin: '0.25rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      }
    });

    flushBullets();

    return result;
  };
  return (
    <>
      <div
        className={`classify-res-comp ${selectedStep === 1 ? 'selected' : ''}`}
        onClick={() => handleRowClick()}
      >
        <div className='classify-res-comp-header'>
          <div className='classify-res-comp-header-left-icon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
            >
              <g clipPath='url(#clip0_1_34180)'>
                <path
                  d='M5.3335 5.33325H5.34016M1.3335 3.46658L1.3335 6.44959C1.3335 6.77572 1.3335 6.93878 1.37034 7.09223C1.403 7.22827 1.45687 7.35833 1.52998 7.47763C1.61243 7.61219 1.72773 7.72749 1.95833 7.95809L7.07075 13.0705C7.86279 13.8625 8.2588 14.2586 8.71546 14.4069C9.11715 14.5375 9.54984 14.5375 9.95153 14.4069C10.4082 14.2586 10.8042 13.8625 11.5962 13.0705L13.0708 11.596C13.8628 10.804 14.2588 10.4079 14.4072 9.95128C14.5377 9.5496 14.5377 9.1169 14.4072 8.71522C14.2588 8.25856 13.8628 7.86254 13.0708 7.07051L7.95833 1.95809C7.72773 1.72749 7.61243 1.61219 7.47787 1.52973C7.35858 1.45663 7.22852 1.40275 7.09247 1.37009C6.93902 1.33325 6.77596 1.33325 6.44984 1.33325L3.46683 1.33325C2.72009 1.33325 2.34672 1.33325 2.06151 1.47858C1.81063 1.60641 1.60665 1.81038 1.47882 2.06126C1.3335 2.34648 1.3335 2.71985 1.3335 3.46658ZM5.66683 5.33325C5.66683 5.51735 5.51759 5.66658 5.3335 5.66658C5.1494 5.66658 5.00016 5.51735 5.00016 5.33325C5.00016 5.14916 5.1494 4.99992 5.3335 4.99992C5.51759 4.99992 5.66683 5.14916 5.66683 5.33325Z'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </g>
              <defs>
                <clipPath id='clip0_1_34180'>
                  <rect width='16' height='16' fill='white' />
                </clipPath>
              </defs>
            </svg>
          </div>
          <div className='classify-res-comp-header-text'>Classification</div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {cmdt_desc !== '' ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <path
                  d='M13.3327 4L5.99935 11.3333L2.66602 8'
                  stroke='#17B26A'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <g clipPath='url(#clip0_1_34184)'>
                  <path
                    d='M8.00016 3.99992V7.99992L10.6668 9.33325M14.6668 7.99992C14.6668 11.6818 11.6821 14.6666 8.00016 14.6666C4.31826 14.6666 1.3335 11.6818 1.3335 7.99992C1.3335 4.31802 4.31826 1.33325 8.00016 1.33325C11.6821 1.33325 14.6668 4.31802 14.6668 7.99992Z'
                    stroke='#F79009'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_1_34184'>
                    <rect width='16' height='16' fill='white' />
                  </clipPath>
                </defs>
              </svg>
            )}
          </div>
          <div
            className='classify-res-comp-header-left-icon'
            style={{ paddingTop: '2px' }}
          >
            {!isExpand ? (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={() => setIsExpand(!isExpand)}
              >
                <path
                  d='M10 12L6 8L10 4'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                onClick={() => setIsExpand(!isExpand)}
              >
                <path
                  d='M4 6L8 10L12 6'
                  stroke='var(--text-classify)'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            )}
          </div>
        </div>

        {!isExpand ? (
          <p className='classification-status'>
            {cmdt_desc ? cmdt_desc : 'Awaiting information...'}
          </p>
        ) : (
          <>
            <div className='classify-res-comp-body'>
              <div className='classify-res-comp-body-cont'>
                <div className='classify-res-comp-body-cont-sec'>
                  <div className='classify-res-comp-body-cont-sec-txt'>
                    {name}
                  </div>
                  {/* <div className='classify-res-comp-body-cont-sec-icon'>
                    <div className='classify-res-comp-body-cont-sec-icon-txt'>
                      Medium Confidence
                    </div>
                  </div> */}
                </div>
                <div className='classify-res-comp-body-cont-txt'>
                  {hts_code}
                </div>
              </div>
            </div>

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M13.6666 4.85173L7.99998 7.99987M7.99998 7.99987L2.33331 4.85173M7.99998 7.99987L8 14.3332M14 10.7056V5.29419C14 5.06576 14 4.95155 13.9663 4.84968C13.9366 4.75956 13.8879 4.67684 13.8236 4.60705C13.7509 4.52816 13.651 4.47269 13.4514 4.36176L8.51802 1.62102C8.32895 1.51598 8.23442 1.46346 8.1343 1.44287C8.0457 1.42464 7.95431 1.42464 7.8657 1.44287C7.76559 1.46346 7.67105 1.51598 7.48198 1.62102L2.54865 4.36176C2.34896 4.47269 2.24912 4.52816 2.17642 4.60705C2.11211 4.67684 2.06343 4.75957 2.03366 4.84968C2 4.95155 2 5.06576 2 5.29419V10.7056C2 10.934 2 11.0482 2.03366 11.1501C2.06343 11.2402 2.11211 11.3229 2.17642 11.3927C2.24912 11.4716 2.34897 11.5271 2.54865 11.638L7.48198 14.3788C7.67105 14.4838 7.76559 14.5363 7.8657 14.5569C7.95431 14.5751 8.0457 14.5751 8.1343 14.5569C8.23442 14.5363 8.32895 14.4838 8.51802 14.3788L13.4514 11.638C13.651 11.5271 13.7509 11.4716 13.8236 11.3927C13.8879 11.3229 13.9366 11.2402 13.9663 11.1501C14 11.0482 14 10.934 14 10.7056Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M11 6.33333L5 3'
                      stroke='var(--text-classify)'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                  Product Information
                </div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandProd ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandProd(!isExpandProd)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandProd(!isExpandProd)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandProd && (
                <div>
                  {cmdt_desc && (
                    <h2
                      style={{
                        color: 'var(--Text-Main, var(--text-primary))',
                        fontSize: '14px',
                        fontWeight: '400',
                        paddingLeft: '28px',
                      }}
                    >
                      {cmdt_desc}
                    </h2>
                  )}
                </div>
              )}
            </div>

            <div className='export-divider' style={{ display: 'flex' }} />

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='15'
                    height='13'
                    viewBox='0 0 15 13'
                    fill='none'
                  >
                    <path
                      d='M7.41667 11.4167H2.88333C2.1366 11.4167 1.76323 11.4167 1.47801 11.2713C1.22713 11.1435 1.02316 10.9395 0.895325 10.6887C0.75 10.4034 0.75 10.0301 0.75 9.28333V2.88333C0.75 2.1366 0.75 1.76323 0.895325 1.47801C1.02316 1.22713 1.22713 1.02316 1.47801 0.895325C1.76323 0.75 2.1366 0.75 2.88333 0.75H3.15C4.64347 0.75 5.39021 0.75 5.96064 1.04065C6.46241 1.29631 6.87035 1.70426 7.12602 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667M7.41667 11.4167V5.01667M7.41667 11.4167H11.95C12.6967 11.4167 13.0701 11.4167 13.3553 11.2713C13.6062 11.1435 13.8102 10.9395 13.938 10.6887C14.0833 10.4034 14.0833 10.0301 14.0833 9.28333V2.88333C14.0833 2.1366 14.0833 1.76323 13.938 1.47801C13.8102 1.22713 13.6062 1.02316 13.3553 0.895325C13.0701 0.75 12.6967 0.75 11.95 0.75H11.6833C10.1899 0.75 9.44312 0.75 8.87269 1.04065C8.37093 1.29631 7.96298 1.70426 7.70732 2.20603C7.41667 2.77646 7.41667 3.52319 7.41667 5.01667'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                  Section and Chapter Notes
                </div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandSecNotes ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandSecNotes(!isExpandSecNotes)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandSecNotes(!isExpandSecNotes)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandSecNotes && (
                <div style={{ paddingLeft: '28px' }}>
                  {section_chapter_notes && (
                    <div
                      className={`explanatory_notes ${
                        !isExpandedSecNotes ? 'expanded' : ''
                      }`}
                    >
                      {formatExplanatoryNote(section_chapter_notes)}
                    </div>
                  )}
                  {section_chapter_notes && (
                    <div
                      className='expl-notes-btn'
                      onClick={() => setIsExpandedSecNotes(!isExpandedSecNotes)}
                    >
                      {!isExpandedSecNotes ? 'Read More' : 'Read Less'}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className='export-divider' style={{ display: 'flex' }} />

            {/* <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M11.6667 7.33333H8.33333M11.6667 10H8.33333M11.6667 4.66667H8.33333M6 2L6 14M5.2 2H10.8C11.9201 2 12.4802 2 12.908 2.21799C13.2843 2.40973 13.5903 2.71569 13.782 3.09202C14 3.51984 14 4.0799 14 5.2V10.8C14 11.9201 14 12.4802 13.782 12.908C13.5903 13.2843 13.2843 13.5903 12.908 13.782C12.4802 14 11.9201 14 10.8 14H5.2C4.07989 14 3.51984 14 3.09202 13.782C2.71569 13.5903 2.40973 13.2843 2.21799 12.908C2 12.4802 2 11.9201 2 10.8V5.2C2 4.07989 2 3.51984 2.21799 3.09202C2.40973 2.71569 2.71569 2.40973 3.09202 2.21799C3.51984 2 4.0799 2 5.2 2Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>
                  Explanatory Notes
                </div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandExpNotes ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandExpNotes(!isExpandExpNotes)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandExpNotes(!isExpandExpNotes)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandExpNotes && (
                <div style={{ paddingLeft: '28px' }}>
                  {explanatory_notes && (
                    <div
                      className={`explanatory_notes ${
                        !isExpanded ? 'expanded' : ''
                      }`}
                    >
                      {formatExplanatoryNote(explanatory_notes)}
                    </div>
                  )}
                  {explanatory_notes && (
                    <div
                      className='expl-notes-btn'
                      onClick={() => setIsExpanded(!isExpanded)}
                      style={{ marginBottom: '10px' }}
                    >
                      {!isExpanded ? 'Read More' : 'Read Less'}
                    </div>
                  )}
                </div>
              )}
            </div> */}

            {/* <div className='export-divider' style={{ display: 'flex' }} /> */}

            <div className='classify-res-comp-sec-col'>
              <div className='classify-res-comp-sec'>
                <div className='classify-res-comp-sec-left'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                  >
                    <path
                      d='M2 14H14M4 12V6.66665M6.66667 12V6.66665M9.33333 12V6.66665M12 12V6.66665M13.3333 4.66665L8.28267 1.50998C8.18012 1.44589 8.12885 1.41385 8.07386 1.40135C8.02524 1.3903 7.97476 1.3903 7.92614 1.40135C7.87115 1.41385 7.81988 1.44589 7.71733 1.50998L2.66667 4.66665H13.3333Z'
                      stroke='var(--text-classify)'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </div>
                <div className='classify-res-comp-sec-txt'>CROSS Rulings</div>
                <div className='classify-res-comp-sec-left'>
                  {!isExpandCross ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandCross(!isExpandCross)}
                    >
                      <path
                        d='M10 12L6 8L10 4'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='16'
                      height='16'
                      viewBox='0 0 16 16'
                      fill='none'
                      onClick={() => setIsExpandCross(!isExpandCross)}
                    >
                      <path
                        d='M4 6L8 10L12 6'
                        stroke='var(--text-classify)'
                        strokeWidth='1.5'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                  )}
                </div>
              </div>
              {isExpandCross && (
                <div style={{ paddingLeft: '28px' }}>
                  {safeCrossLinks.length > 0 ? (
                    <div className='bite-classify-cross-links'>
                      {safeCrossLinks.map((item, index) => (
                        <div
                          key={`${item.url}-${index}`}
                          className='bite-classify-cross-link-card'
                        >
                          <a
                            href={item.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='bite-classify-cross-link-title'
                          >
                            {item.title || item.url}
                          </a>

                          {typeof item.relevance_percent === 'number' && (
                            <div className='bite-classify-cross-link-relevance'>
                              Relevance: {item.relevance_percent}%
                            </div>
                          )}

                          {item.relevance_note && (
                            <div className='bite-classify-cross-link-note'>
                              {item.relevance_note}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='bite-classify-path-awaiting'>
                      Awaiting information...
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ClassifyResultsComp;
